import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Megaphone, Edit3, Power, PowerOff, ExternalLink, Loader2, Video as VideoIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { listMyAds, listAllAds, createAd, updateAd, deleteAd, listVideos } from '../lib/api';
import { toast } from 'sonner';
import { formatCount } from '../mock';
import { useApp } from '../context/AppContext';

const CREATOR_TYPES = [
  { v: 'promotion',   label: 'Promote a video' },
  { v: 'sponsorship', label: 'Channel sponsor' },
];
const OWNER_TYPES = [
  ...CREATOR_TYPES,
  { v: 'banner',   label: 'Home banner (platform)' },
  { v: 'preroll',  label: 'Pre-roll video ad (platform)' },
  { v: 'external', label: 'External ad server (HTML/script)' },
];

const DEFAULT_PLACEMENT = {
  promotion: 'row_promoted',
  sponsorship: 'channel_sponsor',
  banner: 'home_banner',
  preroll: 'preroll',
  external: 'external_banner',
};

export default function AdManager({ ownerMode = false }) {
  const { categories, user } = useApp();
  const [ads, setAds] = useState([]);
  const [myVideos, setMyVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const blank = () => ({
    type: ownerMode ? 'banner' : 'promotion',
    placement: ownerMode ? 'home_banner' : 'row_promoted',
    headline: '', body: '', media_url: '', link_url: '', video_id: '',
    target_categories: [], external_html: '', active: true, budget: 0,
  });
  const [draft, setDraft] = useState(blank());
  const [editingId, setEditingId] = useState(null);

  const types = ownerMode ? OWNER_TYPES : CREATOR_TYPES;

  const load = async () => {
    setLoading(true);
    try {
      const list = ownerMode ? await listAllAds() : await listMyAds();
      setAds(list);
      if (!ownerMode && user?.channel_id) {
        try { setMyVideos(await listVideos({ channel_id: user.channel_id, limit: 200 })); } catch {}
      }
    } catch (err) { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerMode]);

  const onType = (v) => setDraft(d => ({ ...d, type: v, placement: DEFAULT_PLACEMENT[v] || d.placement }));

  const startEdit = (a) => {
    setDraft({
      type: a.type, placement: a.placement,
      headline: a.headline || '', body: a.body || '',
      media_url: a.media_url || '', link_url: a.link_url || '',
      video_id: a.video_id || '',
      target_categories: a.target_categories || [],
      external_html: a.external_html || '',
      active: !!a.active, budget: a.budget || 0,
    });
    setEditingId(a.id);
    setCreating(true);
  };

  const cancelEdit = () => { setDraft(blank()); setEditingId(null); setCreating(false); };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!draft.headline && draft.type !== 'external') { toast.error('Headline is required'); return; }
    if (draft.type === 'external' && !draft.external_html) { toast.error('External ad HTML is required'); return; }
    try {
      if (editingId) {
        await updateAd(editingId, {
          headline: draft.headline, body: draft.body, media_url: draft.media_url, link_url: draft.link_url,
          target_categories: draft.target_categories, active: draft.active, budget: Number(draft.budget) || 0,
          external_html: draft.external_html,
        });
        toast.success('Ad updated');
      } else {
        await createAd({
          ...draft,
          budget: Number(draft.budget) || 0,
          video_id: draft.video_id || null,
        });
        toast.success('Ad created');
      }
      cancelEdit();
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const toggleActive = async (a) => {
    try { await updateAd(a.id, { active: !a.active }); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const remove = async (a) => {
    if (!window.confirm('Delete this ad?')) return;
    try { await deleteAd(a.id); await load(); toast.success('Deleted'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const totals = useMemo(() => ({
    imp: ads.reduce((s, a) => s + (a.impressions || 0), 0),
    clk: ads.reduce((s, a) => s + (a.clicks || 0), 0),
    spent: ads.reduce((s, a) => s + (a.spent || 0), 0),
  }), [ads]);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading ads…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="row-title text-white">{ownerMode ? 'All ads across the platform' : 'Your ads'}</h3>
          <p className="text-white/60 text-[13px] mt-1">
            {ownerMode
              ? 'Create platform banners, pre-rolls and external ad server snippets. You also see creator promotions and sponsorships.'
              : 'Promote a video in category rows or show a sponsor block on your channel.'}
          </p>
        </div>
        {!creating && <button onClick={() => setCreating(true)} className="btn-primary"><Plus className="w-4 h-4" /> New ad</button>}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-5">
        <StatMini label="Impressions" value={formatCount(totals.imp)} />
        <StatMini label="Clicks" value={formatCount(totals.clk)} />
        <StatMini label="Est. spend" value={'$' + totals.spent.toFixed(2)} />
      </div>

      {creating && (
        <form onSubmit={save} className="card-shell p-6 mt-6 grid lg:grid-cols-[1.2fr_1fr] gap-5">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-[#ffcf52]" />
              <div className="font-semibold">{editingId ? 'Edit ad' : 'New ad'}</div>
            </div>

            <label className="block">
              <span className="text-[12.5px] text-white/70">Ad type</span>
              <Select value={draft.type} onValueChange={onType}>
                <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141418] border-white/10 text-white">
                  {types.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>

            {draft.type !== 'external' && (
              <label className="block">
                <span className="text-[12.5px] text-white/70">Headline</span>
                <input value={draft.headline} onChange={e => setDraft({ ...draft, headline: e.target.value })} placeholder="Short, compelling headline" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ff2e63]/60 text-[14px]" />
              </label>
            )}

            {draft.type !== 'external' && (
              <label className="block">
                <span className="text-[12.5px] text-white/70">Body</span>
                <textarea value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} rows={3} placeholder="Up to a paragraph…" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ff2e63]/60 text-[14px]" />
              </label>
            )}

            {draft.type === 'promotion' && !ownerMode && (
              <label className="block">
                <span className="text-[12.5px] text-white/70">Promote video</span>
                <Select value={draft.video_id || ''} onValueChange={(v) => setDraft({ ...draft, video_id: v })}>
                  <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10"><SelectValue placeholder="Pick one of your videos" /></SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/10 text-white max-h-[260px]">
                    {myVideos.map(v => <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </label>
            )}

            {draft.type !== 'external' && (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[12.5px] text-white/70">Media URL (image)</span>
                  <input value={draft.media_url} onChange={e => setDraft({ ...draft, media_url: e.target.value })} placeholder="https://…" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ff2e63]/60 text-[13px]" />
                </label>
                <label className="block">
                  <span className="text-[12.5px] text-white/70">Link URL</span>
                  <input value={draft.link_url} onChange={e => setDraft({ ...draft, link_url: e.target.value })} placeholder="https://…" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ff2e63]/60 text-[13px]" />
                </label>
              </div>
            )}

            {draft.type === 'external' && (
              <label className="block">
                <span className="text-[12.5px] text-white/70">External ad HTML / script</span>
                <textarea value={draft.external_html} onChange={e => setDraft({ ...draft, external_html: e.target.value })} rows={6} placeholder='<!-- Paste ad server HTML / script here (AdSense, VAST iframe, GAM, Taboola, etc.) -->' className="mt-1 w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ffcf52]/60 text-[12.5px] font-mono" />
                <div className="text-[11.5px] text-white/45 mt-1">Sandboxed: renders inside a secure iframe. Scripts allowed; network calls belong to the ad server.</div>
              </label>
            )}

            {(draft.type === 'banner' || draft.type === 'preroll' || draft.type === 'promotion' || draft.type === 'external') && (
              <label className="block">
                <span className="text-[12.5px] text-white/70">Target categories</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(categories || []).map(c => {
                    const on = draft.target_categories.includes(c.slug);
                    return (
                      <button key={c.slug} type="button" onClick={() => setDraft(d => ({ ...d, target_categories: on ? d.target_categories.filter(x => x !== c.slug) : [...d.target_categories, c.slug] }))} className={`chip ${on ? 'active' : ''}`}>{c.name}</button>
                    );
                  })}
                </div>
                <div className="text-[11.5px] text-white/45 mt-1">Empty = show in every category.</div>
              </label>
            )}

            <div className="flex items-center gap-4 pt-2">
              <label className="inline-flex items-center gap-2 text-[13px]"><Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} /> Active</label>
              <label className="inline-flex items-center gap-2 text-[13px]">Budget ($)
                <input type="number" min="0" value={draft.budget} onChange={e => setDraft({ ...draft, budget: e.target.value })} className="w-24 bg-white/[0.04] border border-white/10 rounded-md px-2 py-1 focus:outline-none focus:border-[#ff2e63]/60 text-[13px]" />
              </label>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button type="submit" className="btn-primary">{editingId ? 'Save changes' : 'Create ad'}</button>
              <button type="button" onClick={cancelEdit} className="btn-ghost">Cancel</button>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-[12px] text-white/55 uppercase tracking-wider mb-2">Preview</div>
            <div className="card-shell overflow-hidden">
              {draft.type === 'external' ? (
                <iframe
                  title="ext-preview"
                  srcDoc={`<!doctype html><html><body style="margin:0;background:transparent;color:#fff;font-family:Inter">${draft.external_html || '<div style="padding:24px;color:#888">Paste an external ad HTML snippet to preview.</div>'}</body></html>`}
                  sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                  style={{ width: '100%', height: 180, border: 0 }}
                />
              ) : (
                <div className="grid grid-cols-[1fr_1.3fr]">
                  <div className="p-4">
                    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-[#ffcf52]"><Megaphone className="w-3 h-3" /> {draft.type === 'preroll' ? 'Ad • Pre-roll' : draft.type === 'sponsorship' ? 'Sponsored by' : 'Sponsored'}</div>
                    <div className="font-display text-[22px] leading-tight mt-1">{draft.headline || 'Your headline'}</div>
                    {draft.body && <p className="text-white/70 text-[12.5px] mt-1 line-clamp-3">{draft.body}</p>}
                  </div>
                  <div className="relative min-h-[140px]">
                    <img src={draft.media_url || 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=900'} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Ads table */}
      <div className="card-shell mt-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead className="bg-white/[0.03] text-white/55">
              <tr>
                <th className="text-left px-5 py-2 font-semibold">Ad</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-left px-3 py-2 font-semibold">Placement</th>
                <th className="text-right px-3 py-2 font-semibold">Impr.</th>
                <th className="text-right px-3 py-2 font-semibold">Clicks</th>
                <th className="text-right px-3 py-2 font-semibold">Spent</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {ads.map(a => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-8 rounded overflow-hidden bg-white/5 grid place-items-center shrink-0">
                        {a.type === 'external' ? <ExternalLink className="w-3.5 h-3.5 text-white/60" /> : a.media_url ? <img src={a.media_url} alt="" className="w-full h-full object-cover" /> : <Megaphone className="w-3.5 h-3.5 text-white/50" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold line-clamp-1">{a.headline || (a.type === 'external' ? 'External ad' : '—')}</div>
                        {a.link_url && <div className="text-[11.5px] text-white/50 line-clamp-1">{a.link_url}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 capitalize">{a.type}</td>
                  <td className="px-3 py-3 text-white/65">{a.placement}</td>
                  <td className="px-3 py-3 text-right">{formatCount(a.impressions || 0)}</td>
                  <td className="px-3 py-3 text-right">{formatCount(a.clicks || 0)}</td>
                  <td className="px-3 py-3 text-right">${(a.spent || 0).toFixed(2)}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => toggleActive(a)} className={`chip ${a.active ? 'active' : ''}`}>
                      {a.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />} {a.active ? 'Active' : 'Paused'}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => startEdit(a)} className="text-white/60 hover:text-white mr-3" aria-label="Edit"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(a)} className="text-white/60 hover:text-[#ff6b8a]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {ads.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-white/50">No ads yet. {!creating && <button onClick={() => setCreating(true)} className="text-[#ff6b8a] hover:underline">Create your first one</button>}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="card-shell p-4">
      <div className="text-[12px] text-white/55 uppercase tracking-wider">{label}</div>
      <div className="font-display text-[26px] mt-1 leading-none">{value}</div>
    </div>
  );
}
